import { useEffect, useRef, useState } from 'react';
import { getSessionLiveEstimate } from '../api/sessionsApi';
import { isLiveEstimate, liveEstimateAsOfMs } from '../utils/liveEstimate';

/** Not more than one read per energy change in this window. */
export const LIVE_ESTIMATE_MIN_INTERVAL_MS = 5000;
/** The steady read while the session is active. */
export const LIVE_ESTIMATE_POLL_MS = 30000;
/** A per-minute tariff is a countdown on the budget: read it more often. */
export const LIVE_ESTIMATE_POLL_PER_MINUTE_MS = 10000;

/**
 * The backend's live estimate for the session on the monitoring step
 * (sw-ev-api docs/SESSION_LIVE_ESTIMATE.md).
 *
 * Reads it on mount, again whenever the delivered energy changes (at most
 * once per 5 s) and every 30 s while the session is active. It stops once
 * the session has ended — `ended` from the caller, or `phase: ENDED` from
 * the backend. A failed read is silent: the last good estimate stays, and
 * with none the screen shows "Calculating". A response older than the one
 * already shown (`asOf`) is dropped.
 *
 * @param {object} params
 * @param {string|null} params.sessionUid
 * @param {number|null} [params.energyKwh] - delivered energy; a change triggers a read.
 * @param {boolean} [params.ended] - the session has ended; stop reading.
 * @param {number} [params.pollMs] - the steady read interval.
 * @returns {{ liveEstimate: object|null }}
 */
export default function useLiveEstimate({ sessionUid, energyKwh = null, ended = false, pollMs = LIVE_ESTIMATE_POLL_MS } = {}) {
  // Keyed by session, so one session's estimate is never shown for another.
  const [held, setHeld] = useState({ uid: null, estimate: null });
  const lastFetchAt = useRef(0);
  const throttleTimer = useRef(null);
  const fetchRef = useRef(null);
  const seenEnergy = useRef(energyKwh);

  const liveEstimate = held.uid === sessionUid ? held.estimate : null;
  const active = !!sessionUid && !ended && liveEstimate?.phase !== 'ENDED';

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;

    const fetchNow = () => {
      lastFetchAt.current = Date.now();
      Promise.resolve()
        .then(() => getSessionLiveEstimate(sessionUid))
        .then((result) => {
          if (cancelled || !result?.ok || !isLiveEstimate(result.liveEstimate)) return;
          const next = result.liveEstimate;
          setHeld((prev) => {
            if (prev.uid === sessionUid && prev.estimate) {
              const shown = liveEstimateAsOfMs(prev.estimate);
              const incoming = liveEstimateAsOfMs(next);
              if (shown != null && incoming != null && incoming < shown) return prev; // out of order
            }
            return { uid: sessionUid, estimate: next };
          });
        })
        .catch(() => { /* silent: the screen keeps what it has */ });
    };
    fetchRef.current = fetchNow;

    fetchNow();
    const interval = setInterval(fetchNow, pollMs);

    return () => {
      cancelled = true;
      fetchRef.current = null;
      clearInterval(interval);
      if (throttleTimer.current) {
        clearTimeout(throttleTimer.current);
        throttleTimer.current = null;
      }
    };
  }, [active, sessionUid, pollMs]);

  // Energy moved: read again, but not more than once in five seconds.
  useEffect(() => {
    if (Object.is(seenEnergy.current, energyKwh)) return; // the mount read covers the first value
    seenEnergy.current = energyKwh;
    if (!active || !fetchRef.current || throttleTimer.current) return;
    const wait = LIVE_ESTIMATE_MIN_INTERVAL_MS - (Date.now() - lastFetchAt.current);
    if (wait <= 0) {
      fetchRef.current();
      return;
    }
    throttleTimer.current = setTimeout(() => {
      throttleTimer.current = null;
      fetchRef.current?.();
    }, wait);
  }, [energyKwh, active]);

  return { liveEstimate };
}
